import {z} from 'zod';
import {isReleasedNewRegisterFlow} from '../../../config/unReleasePage';
import {emailRegex} from '../../../shared/services/emailValidation';
import {addressFormShape, refineAddress} from '../address/schema';
import {
  DevelopmentExperienceBodyLength,
  DevelopmentExperienceFormLength,
} from '../mypage/constants';
import {
  DEGREE_TYPE_LIST,
  UNIVERSITY_TYPE_LIST,
  type UniversityType,
} from '../university/constants';
import {
  HITOKOTO_PR_MESSAGE_LENGTH,
  TOEFL_IBT,
  TOEFL_ITP,
  TOEFL_TYPE,
  TOEFL_TYPE_LIST,
  TOEIC,
} from '../user/constants';
import {REGISTER_STAGE_LIST} from './constants';

export const CheckPrivateEmailExistResponseSchema = z.object({
  exists: z.boolean(),
});

export const CheckAcEmailResponseSchema = z.object({
  valid: z.boolean(),
  exists: z.boolean(),
});

export const GetIntroduceCampaignResponseSchema = z.object({
  message: z.string(),
});

export const RegisterAcEmailRequestSchema = z.object({
  email: z
    .string()
    .regex(emailRegex, 'メールアドレスの形式で入力してください')
    .min(1, {message: '必須項目です'})
    .max(100, {message: '100文字以内で入力してください'}), // DB依存
  userIntroduceCampaignID: z.number(),
  introducerID: z.string(),
  registerType: z.number(),
  inflowPath: z.string().nullable(),
  // 期限切れリマインドメール経由の登録を計測するための暗号化済み送信ログID(rk)。リマインド以外の登録では送らない
  rk: z.string().optional(),
  // プロモーションキャンペーン計測用コード(pcid)。?pcid= 付き来訪をCookieで保持し登録時に渡す
  promotionCampaignCode: z.string().optional(),
});

export const RegisterAcEmailResponseSchema = z.string();

export const RegisterMailUnsubscribeResponseSchema = z.string();

export const VerifyEmailResponseSchema = z.object({
  stage: z.enum(REGISTER_STAGE_LIST),
});

// 文字列も受けるのは、APIが旧仕様を返す間にスキーマ不一致で失敗扱いにすると
// 認証済み・mail_key消費済みなのに失敗表示となり再試行できなくなるため。
export const VerifyAcEmailResponseSchema = z.union([
  z.string(),
  z.object({redirectUrl: z.string().optional()}),
]);

export const VerifyPrivateEmailResponseSchema = z.string();

// GeneralProfile
// acEmail/isAcEmailVerifiedは新フローでのみ返るためoptional。privateEmailは新旧どちらでも返る
export const GeneralProfileResponseSchema = z.object({
  acEmail: z.string().optional(),
  isAcEmailVerified: z.boolean().optional(),
  privateEmail: z.string(),
  firstName: z.string(),
  familyName: z.string(),
  yomiFirstName: z.string(),
  yomiFamilyName: z.string(),
  alphabetFirstName: z.string(),
  alphabetFamilyName: z.string(),
  sex: z.number(),
  birthday: z.string(),
  telNumber: z
    .string()
    .regex(/^[\d-]*$/, '半角数字とハイフンのみで入力してください'),
  isOverseas: z.boolean(),
  prefectureID: z.number().nullable(),
  zipCode: z.string(),
  address1: z.string(),
  address2: z.string(),
  building: z.string(),
  // もう一つの住所(帰省先/休暇先)。フラット命名で返る。
  // 未入力時は都道府県がnull、テキストは空文字(現住所フィールドと同じ扱い)
  alternatePrefectureID: z.number().nullable(),
  alternateZipCode: z.string(),
  alternateAddress1: z.string(),
  alternateAddress2: z.string(),
  alternateBuilding: z.string(),
  stage: z.enum(REGISTER_STAGE_LIST),
});

const generalProfileRequestBaseShape = {
  password: z
    .string()
    // マルチバイト文字を考慮し、文字数でカウントする
    .regex(/^.{8,}$/u, {
      message: 'パスワードが短すぎます。8文字以上64文字以内にしてください',
    })
    .regex(/^.{0,64}$/u, {
      message: 'パスワードが長すぎます。8文字以上64文字以内にしてください',
    })
    .min(1, {message: '必須項目です'}),
  firstName: z.string().min(1, {message: '必須項目です'}),
  familyName: z.string().min(1, {message: '必須項目です'}),
  yomiFirstName: z
    .string()
    .min(1, {message: '必須項目です'})
    .regex(/^[ぁ-ゔー]*$/, {message: 'ひらがなで入力してください'}),
  yomiFamilyName: z
    .string()
    .min(1, {message: '必須項目です'})
    .regex(/^[ぁ-ゔー]*$/, {message: 'ひらがなで入力してください'}),
  alphabetFirstName: z
    .string()
    .regex(/^[a-zA-Z]+$/, '半角アルファベットで入力してください')
    .min(1, {message: '必須項目です'}),
  alphabetFamilyName: z
    .string()
    .regex(/^[a-zA-Z]+$/, '半角アルファベットで入力してください')
    .min(1, {message: '必須項目です'}),
  sex: z.coerce
    .number()
    .min(1, {message: '必須項目です'})
    .max(2, {message: '必須項目です'}),
  birthday: z.coerce
    .string()
    .nullable()
    .refine((day) => day !== null, '必須項目です'),
  telNumber: z
    .string()
    .regex(/^[\d-]+$/, '半角数字とハイフン以外の文字が含まれています')
    .min(1, {message: '必須項目です'}),
};

// 大学メールアドレス(acEmail)は任意で受け付ける
export const GeneralProfileRequestSchema = refineAddress(
  z.object({
    ...generalProfileRequestBaseShape,
    ...addressFormShape,
    // 任意入力のため空文字を許容し、入力時のみ形式・文字数を検証する
    acEmail: z.union([
      z.literal(''),
      z
        .string()
        .regex(emailRegex, 'メールアドレスの形式で入力してください')
        .max(100, {message: '100文字以内で入力してください'}),
    ]),
  }),
);

export const GeneralProfileResponse = z.string();

// EDUCATION
export const UniversityResponseSchema = z.object({
  degreeType: z.enum(DEGREE_TYPE_LIST),
  universityType: z.enum(UNIVERSITY_TYPE_LIST),
  universityID: z.number().nullable(),
  otherUniversityName: z.string().nullable(),
  majorID: z.number().nullable(),
  majorName: z.string().nullable(),
  subjectID: z.number().nullable(),
  subjectName: z.string().nullable(),
  bunRi: z.number(),
  enrollYear: z.number(),
  enrollMonth: z.number(),
  graduateYear: z.number(),
  graduateMonth: z.number(),
  laboratoryName: z.string().nullable(),
  professorID: z.number().nullable(),
  professorName: z.string().nullable(),
  theme: z.string().nullable(),
});

const UniversityRequestBaseSchema = z.object({
  degreeType: z.enum(DEGREE_TYPE_LIST),
  universityType: z.enum(UNIVERSITY_TYPE_LIST),
  universityID: z.coerce.number().nullable(),
  otherUniversityName: z.string().nullable(),
  majorID: z.coerce.number().nullable(),
  majorName: z.string().nullable(),
  subjectID: z.coerce.number().nullable(),
  subjectName: z.string().nullable(),
  bunRi: z.coerce
    .number()
    .min(1, {message: '必須項目です'})
    .max(2, {message: '必須項目です'}),
  enrollYear: z.coerce
    .number({invalid_type_error: '半角数字で入力してください'})
    .min(1970, {message: '値が不正です'})
    .max(2100, {message: '値が不正です'}),
  enrollMonth: z.coerce
    .number({invalid_type_error: '半角数字で入力してください'})
    .min(1, {message: '値が不正です'})
    .max(12, {message: '値が不正です'}),
  graduateYear: z.coerce
    .number({invalid_type_error: '半角数字で入力してください'})
    .min(1970, {message: '値が不正です'})
    .max(2100, {message: '値が不正です'}),
  graduateMonth: z.coerce
    .number({invalid_type_error: '半角数字で入力してください'})
    .min(1, {message: '値が不正です'})
    .max(12, {message: '値が不正です'}),
  laboratoryName: z.string().nullable(),
  professorID: z.coerce.number().nullable(),
  professorName: z.string().nullable(),
  theme: z.string().nullable(),
});

const checkUniversity = (
  data: z.infer<typeof UniversityRequestBaseSchema>,
  inc: UniversityType[],
  ext: UniversityType[],
) => {
  if (inc.includes(data.universityType)) {
    return true;
  }

  return (
    ext.includes(data.universityType) &&
    (data.universityID || data.otherUniversityName)
  );
};

const checkMajor = (
  data: z.infer<typeof UniversityRequestBaseSchema>,
  inc: UniversityType[],
  ext: UniversityType[],
) => {
  if (inc.includes(data.universityType)) {
    return true;
  }

  return ext.includes(data.universityType) && (data.majorID || data.majorName);
};

const checkSubject = (
  data: z.infer<typeof UniversityRequestBaseSchema>,
  inc: UniversityType[],
  ext: UniversityType[],
) => {
  if (inc.includes(data.universityType)) {
    return true;
  }

  return (
    ext.includes(data.universityType) && (data.subjectID || data.subjectName)
  );
};

export const UniversityRequestSchema = UniversityRequestBaseSchema.refine(
  (data) => checkUniversity(data, ['university', 'foreign'], ['kosen']),
  {
    message: '学校名が選択されていません',
    path: ['universityID'],
  },
)
  .refine(
    (data) => checkUniversity(data, ['kosen'], ['university', 'foreign']),
    {
      message: '大学名が選択されていません',
      path: ['universityID'],
    },
  )
  .refine(
    (data) => checkUniversity(data, ['university', 'foreign'], ['kosen']),
    {
      message: '学校名が選択されていません',
      path: ['otherUniversityName'],
    },
  )
  .refine(
    (data) => checkUniversity(data, ['kosen'], ['university', 'foreign']),
    {
      message: '大学名が選択されていません',
      path: ['otherUniversityName'],
    },
  )
  .refine((data) => checkMajor(data, ['university', 'foreign'], ['kosen']), {
    message: '学科名が選択されていません',
    path: ['majorID'],
  })
  .refine((data) => checkMajor(data, ['kosen'], ['university', 'foreign']), {
    message: '学部名が選択されていません',
    path: ['majorID'],
  })
  .refine((data) => checkMajor(data, ['university', 'foreign'], ['kosen']), {
    message: '学科名が選択されていません',
    path: ['majorName'],
  })
  .refine((data) => checkMajor(data, ['kosen'], ['university', 'foreign']), {
    message: '学部名が選択されていません',
    path: ['majorName'],
  })
  .refine((data) => checkSubject(data, ['university', 'foreign'], ['kosen']), {
    message: 'コース名が選択されていません',
    path: ['subjectID'],
  })
  .refine((data) => checkSubject(data, ['kosen'], ['university', 'foreign']), {
    message: '学科名が選択されていません',
    path: ['subjectID'],
  })
  .refine((data) => checkSubject(data, ['university', 'foreign'], ['kosen']), {
    message: 'コース名が選択されていません',
    path: ['subjectName'],
  })
  .refine((data) => checkSubject(data, ['kosen'], ['university', 'foreign']), {
    message: '学科名が選択されていません',
    path: ['subjectName'],
  })
  .refine(
    (data) => {
      const startDate = Number(
        `${data.enrollYear}${String(data.enrollMonth).padStart(2, '0')}`,
      );
      const endDate = Number(
        `${data.graduateYear}${String(data.graduateMonth).padStart(2, '0')}`,
      );

      return startDate <= endDate;
    },
    {
      message: '終了年月を開始年月より未来の年月にしてください。',
      path: ['universityStartEndDate'],
    },
  );

// 高校
const HighSchoolBaseSchema = z.object({
  highSchoolName: z.string(),
  highSchoolEnrollYear: z.number(),
  highSchoolEnrollMonth: z.number(),
  highSchoolGraduateYear: z.number(),
  highSchoolGraduateMonth: z.number(),
});

const HighSchoolRequestSchema = z.object({
  highSchoolName: z.string().min(1, {message: '必須項目です'}),
  highSchoolEnrollYear: z.coerce
    .number({invalid_type_error: '半角数字で入力してください'})
    .min(1970, {message: '必須項目です'}),
  highSchoolEnrollMonth: z.coerce
    .number({invalid_type_error: '半角数字で入力してください'})
    .min(1, {message: '必須項目です'})
    .max(12, {message: '必須項目です'}),
  highSchoolGraduateYear: z.coerce
    .number({invalid_type_error: '半角数字で入力してください'})
    .min(1970, {message: '必須項目です'}),
  highSchoolGraduateMonth: z.coerce
    .number({invalid_type_error: '半角数字で入力してください'})
    .min(1, {message: '必須項目です'})
    .max(12, {message: '必須項目です'}),
});

export const GraduationYearTempResponseSchema = z.object({
  graduationYear: z.number(),
});

export const RegisterEducationResponseSchema = z
  .object({
    grade: z.number(),
    stage: z.enum(REGISTER_STAGE_LIST),
    educationUniversityTemps: z.array(UniversityResponseSchema),
  })
  .merge(HighSchoolBaseSchema);

export const RegisterEducationRequestSchema = z
  .object({
    grade: z.coerce
      .number()
      .min(1, {message: '必須項目です'})
      .max(13, {message: '必須項目です'}),
    educationUniversityTemps: z.array(UniversityRequestSchema),
  })
  .merge(HighSchoolRequestSchema)
  .refine(
    (data) => {
      const startDate = Number(
        `${data.highSchoolEnrollYear}${String(
          data.highSchoolEnrollMonth,
        ).padStart(2, '0')}`,
      );
      const endDate = Number(
        `${data.highSchoolGraduateYear}${String(
          data.highSchoolGraduateMonth,
        ).padStart(2, '0')}`,
      );

      return startDate <= endDate;
    },
    {
      message: '終了年月を開始年月より未来の年月にしてください。',
      path: ['highschoolStartEndDate'],
    },
  );

export const PostRegisterEducationResponseSchema = z.string();

// SKILL
export const RegisterSkillResponseSchema = z.object({
  toeicPoint: z.number().nullable(),
  toeflType: z.enum(TOEFL_TYPE_LIST).nullable(),
  toeflPoint: z.number().nullable(),
  languageSkills: z.array(
    z.object({
      languageID: z.number(),
      languageName: z.string(),
      levelID: z.number(),
      levelName: z.string(),
    }),
  ),
  onelineMessage1: z.string().nullable(),
  onelineMessage2: z.string().nullable(),
  onelineMessage3: z.string().nullable(),
  stage: z.enum(REGISTER_STAGE_LIST),
});

const HitokotoPrMessageSchema = z.string().max(HITOKOTO_PR_MESSAGE_LENGTH, {
  message: `${HITOKOTO_PR_MESSAGE_LENGTH}文字以内で入力してください`,
});

const toeicScoreValidationMessage = `点数の範囲は${TOEIC.MIN} ~ ${TOEIC.MAX}です`;

export const RegisterSkillRequestSchema = z
  .object({
    toeicPoint: z.coerce
      .number({invalid_type_error: '半角数字で入力してください'})
      .min(TOEIC.MIN, {message: toeicScoreValidationMessage})
      .max(TOEIC.MAX, {message: toeicScoreValidationMessage})
      .nullable(),
    toeflType: z.enum(TOEFL_TYPE_LIST).nullable(),
    toeflPoint: z.coerce
      .number({invalid_type_error: '半角数字で入力してください'})
      .nullable(),
    languageSkills: z.array(
      z.object({
        languageID: z.coerce
          .number()
          .min(1, {message: '選択するか削除してください'}),
        levelID: z.coerce
          .number()
          .min(1, {message: '選択するか削除してください'}),
      }),
    ),
    onelineMessage1: HitokotoPrMessageSchema,
    onelineMessage2: HitokotoPrMessageSchema,
    onelineMessage3: HitokotoPrMessageSchema,
  })
  .refine(
    (data) => {
      if (!data.toeflPoint) return true;
      if (
        data.toeflType === TOEFL_TYPE.IBT &&
        (data.toeflPoint < TOEFL_IBT.MIN || data.toeflPoint > TOEFL_IBT.MAX)
      ) {
        return false;
      }

      return true;
    },
    {
      message: `点数の範囲は${TOEFL_IBT.MIN} ~ ${TOEFL_IBT.MAX}です`,
      path: ['toeflPoint'],
    },
  )
  .refine(
    (data) => {
      if (!data.toeflPoint) return true;
      if (
        data.toeflType === TOEFL_TYPE.ITP &&
        (data.toeflPoint < TOEFL_ITP.MIN || data.toeflPoint > TOEFL_ITP.MAX)
      ) {
        return false;
      }

      return true;
    },
    {
      message: `点数の範囲は${TOEFL_ITP.MIN} ~ ${TOEFL_ITP.MAX}です`,
      path: ['toeflPoint'],
    },
  );

export type RegisterSkillRequestType = z.infer<
  typeof RegisterSkillRequestSchema
>;

export const PostRegisterSkillResponseSchema = z.string();

// IT_SKILL
export const HashTagSchema = z.object({
  hashtagID: z.number(),
  hashtagName: z.string().nullable(),
});

export const DevelopmentExperienceSchema = z.object({
  body: z.string(),
  categoryTag: HashTagSchema.nullable(),
  programmingLanguageTags: z.array(HashTagSchema),
  libraryFrameworkTags: z.array(HashTagSchema),
  otherTags: z.array(HashTagSchema),
  startYear: z.number().nullable(),
  startMonth: z.number().nullable(),
  endYear: z.number().nullable(),
  endMonth: z.number().nullable(),
});

export type DevelopmentExperienceFormType = z.infer<
  typeof DevelopmentExperienceSchema
>;

export const ItSkillSchema = z.object({
  experienceSummaryTags: z.array(HashTagSchema),
  experiences: z
    .array(DevelopmentExperienceSchema)
    .max(DevelopmentExperienceFormLength, {
      message: `最大${DevelopmentExperienceFormLength}つまでしか登録できません`,
    }),
  githubID: z.string().nullable(),
  qiitaID: z.string().nullable(),
  atCoderID: z.string().nullable(),
  kaggleID: z.string().nullable(),
  mySites: z
    .array(z.string())
    .max(20, {message: '最大20個までしか登録できません'}),
  publishLevel: z.number().min(0).max(1),
  hasEngineerInterest: z
    .boolean()
    .nullable()
    .refine((val) => val !== undefined, {
      message: '必須項目です',
    })
    .transform((val) => (val === undefined ? undefined : val)),
});

const PostDevelopmentExperienceSchema = z.object({
  body: z.string(),
  categoryID: z.number().nullable(),
  usedTechnologyIDs: z.array(z.number()),
  startYear: z.number().nullable(),
  startMonth: z.number().nullable(),
  endYear: z.number().nullable(),
  endMonth: z.number().nullable(),
});

export const PostItSkillTempSchema = z.object({
  experienceSummaryIDs: z.array(z.number()).nullable(),
  experiences: z.array(PostDevelopmentExperienceSchema),
  githubID: z.string().nullable(),
  qiitaID: z.string().nullable(),
  atCoderID: z.string().nullable(),
  kaggleID: z.string().nullable(),
  mySites: z.array(z.string()).nullable(),
  publishLevel: z.number().nullable(),
  hasEngineerInterest: z.boolean().nullable(),
});

export const RegisterItSkillResponseSchema = ItSkillSchema.merge(
  z.object({
    stage: z.enum(REGISTER_STAGE_LIST),
  }),
);

export type RegisterItSkillResponse = z.infer<
  typeof RegisterItSkillResponseSchema
>;
export const PostRegisterItSkillResponseSchema = z.string();

const DevelopmentExperienceFormSchema = z
  .object({
    body: z.string().max(DevelopmentExperienceBodyLength, {
      message: `最大${DevelopmentExperienceBodyLength}文字までです`,
    }),
    categoryTag: HashTagSchema,
    programmingLanguageTags: z.array(HashTagSchema),
    libraryFrameworkTags: z.array(HashTagSchema),
    otherTags: z.array(HashTagSchema),
    startDate: z.string(),
    endDate: z.string().optional(),
  })
  .refine(
    (schema) => {
      // 開発経験内の項目が全て空の場合はOK
      if (
        schema.body.length === 0 &&
        schema.categoryTag.hashtagID === 0 &&
        schema.programmingLanguageTags.length === 0 &&
        schema.libraryFrameworkTags.length === 0 &&
        schema.otherTags.length === 0 &&
        !schema.startDate &&
        !schema.endDate
      ) {
        return true;
      }

      // 以下の場合はエラー
      // カテゴリ・開発経験の内容・期間が空
      // 「開発言語」「ライブラリ、フレームワーク」「その他」のいずれも入力されていない
      // 「終了年月」は「開始年月」より後 または 空
      return (
        schema.body.length > 0 &&
        schema.categoryTag.hashtagID > 0 &&
        schema.startDate &&
        (schema.programmingLanguageTags.length > 0 ||
          schema.libraryFrameworkTags.length > 0 ||
          schema.otherTags.length > 0)
      );
    },
    {
      message:
        'カテゴリ・開発経験の内容・期間は必須項目です。また開発言語、ライブラリ・フレームワーク、その他のいずれかを入力してください',
    },
  )
  .refine(
    (schema) => {
      if (!schema.startDate || !schema.endDate) return true;
      const startDate = new Date(schema.startDate);
      const endDate = new Date(schema.endDate);
      return startDate <= endDate;
    },
    {message: '開発事例の終了日は開始日より後にしてください'},
  );

export const RegisterItSkillFormSchema = z.object({
  experienceSummaryTags: z.array(HashTagSchema),
  experiences: z
    .array(DevelopmentExperienceFormSchema)
    .max(DevelopmentExperienceFormLength, {
      message: `最大${DevelopmentExperienceFormLength}つまでしか登録できません`,
    }),
  githubID: z.string().nullable(),
  qiitaID: z.string().nullable(),
  atCoderID: z.string().nullable(),
  kaggleID: z.string().nullable(),
  mySites: z
    .array(z.string())
    .max(20, {message: '最大20個までしか登録できません'}),
  publishLevel: z.boolean(),
  hasEngineerInterest: z
    .boolean({
      message: '必須項目です',
    })
    .nullable(),
});

export type RegisterItSkillForm = z.infer<typeof RegisterItSkillFormSchema>;

const WishCompanyItemSchema = z.object({
  companyID: z.number().nullable(),
  companyName: z.string().nullable(),
  industryID: z.number().nullable(),
  wishOrder: z.number(),
});
const WishIntensionItemSchema = z.object({
  intentionID: z.number(),
  intentionName: z.string(),
});

export const GetRegisterWishSchema = z.object({
  jobTypes: z.array(
    z.object({
      jobTypeID: z.number(),
      jobTypeName: z.string(),
      wishOrder: z.number(),
    }),
  ),
  industries: z.array(
    z.object({
      industryID: z.number(),
      industryName: z.string(),
      wishOrder: z.number(),
    }),
  ),
  companies: z.array(WishCompanyItemSchema),
  intentions: z.array(WishIntensionItemSchema),
  stage: z.enum(REGISTER_STAGE_LIST),
});

// 0の時があるので
export const PostRegisterWishSchema = z.object({
  jobTypes: z
    .array(
      z.object({
        jobTypeID: z.number(),
        wishOrder: z.number(),
      }),
    )
    .max(3, {
      message: '最大3つまでしか登録できません',
    }),
  industries: z
    .array(
      z.object({
        industryID: z.number(),
        wishOrder: z.number(),
      }),
    )
    .max(3, {
      message: '最大3つまでしか登録できません',
    }),
  companies: z.array(WishCompanyItemSchema).max(3, {
    message: '最大3つまでしか登録できません',
  }),
  intentions: z
    .array(z.number())
    .min(1, {message: '1つ以上選択してください'})
    .max(5, {message: '最大5つまでしか選択できません'}),
});

export const PostRegisterWishResponseSchema = z.string();

export const GetRegisterFollowCompanySchema = z.object({
  companies: z.array(
    z.object({
      companyID: z.number(),
      companyName: z.string(),
    }),
  ),
  graduationYear: z.number(),
  stage: z.enum(REGISTER_STAGE_LIST),
});

export const PostRegisterFollowCompanySchema = z.object({
  companyIDs: z.array(z.number()).min(8, {
    message: '最低8つ以上選択してください',
  }),
});

export const PostRegisterFollowCompanyResponseSchema = z.string();

export const EnqueteQuestion = z.object({
  id: z.number(),
  type: z.enum(['radio', 'checkbox', 'textarea']),
  sentence: z.string(),
  required: z.boolean(),
  choices: z.array(
    z.object({
      id: z.number(),
      text: z.string(),
    }),
  ),
});

export const GetRegisterEnqueteListSchema = z.object({
  questions: z.array(EnqueteQuestion),
});

export const EnqueteAnswerSchema = z.object({
  questionID: z.number(),
  answer: z.string(),
});

export const PostRegisterFinishSchema = z.object({
  skillCheckHistoryID: z.string().optional(),
  entrysheetBuilderID: z.string().optional(),
  rikunabi: z.boolean(),
  typeShukatsu: z.boolean(),
  careerTasu: z.boolean(),
  minshu: z.boolean(),
  enqueteAnswers: z.array(EnqueteAnswerSchema),
});

export const PostRegisterFinishResponseSchema = z.object({
  accessToken: z.string(),
  accessTokenExpiresIn: z.string(), // ISO 8601形式の文字列
  refreshToken: z.string(),
  refreshTokenExpiresIn: z.string(), // ISO 8601形式の文字列
  needsAcEmailVerifyForCampaign: z.boolean().optional(),
});

export const GetIntroductionCampaignMessageSchema = z.object({
  message: z.string(),
});

const postStudentCardBaseShape = {
  registerType: z.number().nullable(),
  images: z.array(
    z.object({
      imageBase64: z.string().min(1, {message: '必須項目です'}),
      imageFileName: z.string(),
      side: z.boolean(),
    }),
  ),
};

// 新フローでは氏名・メールは認証済みユーザー情報から取得するため送らない
// 旧フローでは氏名・メールをフォーム入力で受け取る
export const PostStudentCardSchema = isReleasedNewRegisterFlow()
  ? z.object({...postStudentCardBaseShape})
  : z.object({
      ...postStudentCardBaseShape,
      email: z
        .string()
        .regex(emailRegex, 'メールアドレスの形式で入力してください'),
      firstName: z.string().min(1, {message: '必須項目です'}),
      familyName: z.string().min(1, {message: '必須項目です'}),
    });

export const PostStudentCardResponseSchema = z.string();

const GetHashtagSchema = z.object({
  id: z.number(),
  name: z.string(),
  usedCount: z.number(),
});

export type GetHashtagType = z.infer<typeof GetHashtagSchema>;

export const GetHashtagListSchema = z.object({
  hashtags: z.array(GetHashtagSchema),
  q: z.string(),
});

const LanguageListSchema = z.array(
  z.object({
    id: z.number(),
    name: z.string(),
  }),
);

export const LanguageMasterSchema = z.object({
  languages: LanguageListSchema,
  q: z.string(),
});

const IntentionSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const IntentionMasterSchema = z.object({
  intentions: z.array(IntentionSchema),
  q: z.string(),
});

export const GetInflowPathSchema = z.object({
  inflowPath: z.string(),
});
